/**
 * Console API client.
 *
 * The console is just another consumer of the public API (PRD §6.1: every panel
 * function has a backend equivalent). It holds a session token, never an API key, and
 * never talks to the engine.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId: string | null,
    readonly retryable: boolean,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'talkium.session';
const ORG_KEY = 'talkium.organization';
const IMPERSONATION_KEY = 'talkium.impersonation';

export const session = {
  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  get organizationId(): string | null {
    return localStorage.getItem(ORG_KEY);
  },
  set(token: string, organizationId?: string) {
    localStorage.setItem(TOKEN_KEY, token);
    if (organizationId) localStorage.setItem(ORG_KEY, organizationId);
  },
  setOrganization(organizationId: string) {
    localStorage.setItem(ORG_KEY, organizationId);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
  },
};

/**
 * A concessão de §65 enquanto ela está ativa neste navegador.
 *
 * Ela mora numa chave PRÓPRIA, ao lado da sessão do operador e nunca por cima dela. Se
 * sobrescrevesse, sair da impersonation exigiria fazer login de novo no plano de
 * operação — e um caminho de volta que custa caro é um caminho que as pessoas deixam
 * de usar. Aqui, sair é apagar uma chave.
 *
 * O token some quando a aba é fechada (`sessionStorage`, não `localStorage`): ele vale
 * no máximo duas horas e dá acesso à conta de um cliente, então não é coisa para ficar
 * em repouso num navegador esquecido.
 */
export const impersonation = {
  get token(): string | null {
    return sessionStorage.getItem(IMPERSONATION_KEY);
  },
  get active(): boolean {
    return sessionStorage.getItem(IMPERSONATION_KEY) !== null;
  },
  enter(token: string, organizationId: string) {
    sessionStorage.setItem(IMPERSONATION_KEY, token);
    localStorage.setItem(ORG_KEY, organizationId);
  },
  leave() {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  },
};

/**
 * Qual credencial mandar.
 *
 * O plano de operação é sempre do operador; o resto do console é de quem ele estiver
 * vendo. A regra é por caminho, num lugar só: espalhar a escolha por cada chamada
 * daria, mais cedo ou mais tarde, uma chamada de admin feita com o token do cliente —
 * ou, pior, o contrário.
 */
function credentialFor(path: string): string | null {
  if (path.startsWith('/admin/v1/')) return session.token;
  return impersonation.token ?? session.token;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Cabeçalhos extras. O plano de operação usa isto para o segundo fator (§71). */
  headers?: Record<string, string>;
}

/** Fired on 401 so the shell can send the user back to the login screen. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  const credential = credentialFor(path);
  if (credential) headers.authorization = `Bearer ${credential}`;
  // The console always acts inside one organization; the switcher sets this.
  if (session.organizationId) headers['x-talkium-organization'] = session.organizationId;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  Object.assign(headers, options.headers ?? {});

  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const requestId = response.headers.get('x-request-id');

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    if (response.status === 401) onUnauthorized();
    const body = payload as {
      error?: { code?: string; message?: string; retryable?: boolean; details?: unknown };
    } | null;
    throw new ApiError(
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `A API respondeu ${response.status}.`,
      response.status,
      requestId,
      body?.error?.retryable ?? false,
      body?.error?.details,
    );
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Opens an SSE stream (PRD §123).
 *
 * Uses fetch streaming rather than `EventSource` deliberately: EventSource cannot send
 * headers, which would force the session token into the query string, where it lands in
 * access logs, browser history and referrers. A header keeps it out of all three.
 */
export function stream(
  path: string,
  handlers: { [event: string]: (data: Record<string, unknown>) => void },
): () => void {
  const controller = new AbortController();

  void (async () => {
    try {
      const headers: Record<string, string> = { accept: 'text/event-stream' };
      const credential = credentialFor(path);
      if (credential) headers.authorization = `Bearer ${credential}`;
      if (session.organizationId) headers['x-talkium-organization'] = session.organizationId;

      const response = await fetch(path, { headers, signal: controller.signal });
      if (!response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line; a partial frame stays in the buffer.
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          dispatchFrame(buffer.slice(0, boundary), handlers);
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch {
      // An aborted or dropped stream is a normal end, not an error to surface.
    }
  })();

  return () => controller.abort();
}

function dispatchFrame(
  raw: string,
  handlers: { [event: string]: (data: Record<string, unknown>) => void },
): void {
  let event = 'message';
  let data = '';

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }

  const handler = handlers[event];
  if (!handler || !data) return;
  try {
    handler(JSON.parse(data) as Record<string, unknown>);
  } catch {
    // A malformed frame must not tear down the whole stream.
  }
}
